// pages/support/knowledge/ImportKnowledgeListPage.tsx
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Upload, CheckCircle, XCircle, Loader2, ArrowLeft } from "lucide-react";
import PageListLayout from "../../../../layouts/PageListLayout";
import useKnowledge from "../../../../hooks/support/knowledge/useKnowledge";
import { mockKnowledgeArticles } from "../../../../mocks/mockKnowledgeArticles";
import { KnowledgeArticle } from "../../../../types/features/support/KnowledgeArticle";
import { alertSuccess, alertError } from "../../../../helpers/alertError";

export default function ImportKnowledgeListPage() {
  const navigate = useNavigate();
  const { createArticle, loading } = useKnowledge();
  const [importedIds, setImportedIds] = useState<string[]>([]);
  const [importingAll, setImportingAll] = useState(false);
  const [importingId, setImportingId] = useState<string | null>(null);

  const availableArticles = mockKnowledgeArticles.filter((article) => !importedIds.includes(article.id));

  const handleImport = async (article: KnowledgeArticle) => {
    setImportingId(article.id);
    try {
      const result = await createArticle({
        title: article.title,
        slug: article.slug,
        category: article.category,
        objective: article.objective,
        description: article.description,
        causes: article.causes,
        signs: article.signs,
        agent_procedure: article.agent_procedure,
        technical_procedure: article.technical_procedure,
        errors_to_avoid: article.errors_to_avoid,
        escalation: article.escalation,
        client_response: article.client_response,
        product_doc: article.product_doc,
        responsible_department: article.responsible_department,
        keywords: article.keywords,
        is_active: article.is_active,
      });
      if (result) {
        setImportedIds([...importedIds, article.id]);
        alertSuccess(`"${article.title}" importé avec succès`);
      } else {
        alertError(`Erreur lors de l'import de "${article.title}"`);
      }
    } catch (error) {
      alertError(`Erreur lors de l'import de "${article.title}"`);
    } finally {
      setImportingId(null);
    }
  };

  const handleImportAll = async () => {
    setImportingAll(true);
    let successCount = 0;
    let failCount = 0;

    for (const article of availableArticles) {
      try {
        const result = await createArticle({
          title: article.title,
          slug: article.slug,
          category: article.category,
          objective: article.objective,
          description: article.description,
          causes: article.causes,
          signs: article.signs,
          agent_procedure: article.agent_procedure,
          technical_procedure: article.technical_procedure,
          errors_to_avoid: article.errors_to_avoid,
          escalation: article.escalation,
          client_response: article.client_response,
          product_doc: article.product_doc,
          responsible_department: article.responsible_department,
          keywords: article.keywords,
          is_active: article.is_active,
        });
        if (result) {
          successCount++;
          setImportedIds((prev) => [...prev, article.id]);
        } else {
          failCount++;
        }
      } catch (error) {
        failCount++;
      }
    }

    setImportingAll(false);
    if (failCount === 0) {
      alertSuccess(`${successCount} article(s) importé(s) avec succès`);
    } else {
      alertError(`${successCount} importé(s), ${failCount} échec(s)`);
    }
  };

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case "Problème":
        return { label: "Problème", className: "bg-red-100 text-red-700" };
      case "Procédure Agent":
        return { label: "Procédure agent", className: "bg-blue-100 text-blue-700" };
      case "Procédure Technique":
        return { label: "Procédure technique", className: "bg-purple-100 text-purple-700" };
      case "Escalade":
        return { label: "Escalade", className: "bg-orange-100 text-orange-700" };
      case "Réponse Client":
        return { label: "Réponse client", className: "bg-green-100 text-green-700" };
      default:
        return { label: category || "Article", className: "bg-gray-100 text-gray-700" };
    }
  };

  return (
    <PageListLayout
      title="Importer des articles"
      actions={
        <div className="flex gap-2">
          <button
            onClick={() => navigate("/support/knowledge")}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour
          </button>
          {availableArticles.length > 0 && (
            <button
              onClick={handleImportAll}
              disabled={importingAll}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 disabled:opacity-50"
            >
              {importingAll ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Importation...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  Importer tout ({availableArticles.length})
                </>
              )}
            </button>
          )}
        </div>
      }
    >
      <div className="p-6">
        {availableArticles.length === 0 ? (
          <div className="text-center py-12">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900">Tous les articles sont importés</h3>
            <p className="text-gray-500 mt-2">La base de connaissances est à jour.</p>
            <button onClick={() => navigate("/support/knowledge")} className="mt-4 text-green-600 hover:text-green-700">
              Retour à la base de connaissances
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-gray-500">{availableArticles.length} article(s) en attente d'importation</p>
            {availableArticles.map((article) => {
              const category = getCategoryLabel(article.category);
              const isImporting = importingId === article.id;
              return (
                <div
                  key={article.id}
                  className="bg-white border border-gray-200 rounded-xl p-4 flex items-center justify-between hover:shadow-sm transition-all"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs px-2 py-1 rounded-full ${category.className}`}>{category.label}</span>
                    </div>
                    <h3 className="font-medium text-gray-900">{article.title}</h3>
                    <p className="text-sm text-gray-500">{article.description}</p>
                    {article.keywords && article.keywords.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {article.keywords.slice(0, 3).map((keyword, idx) => (
                          <span key={idx} className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                            {keyword}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => handleImport(article)}
                    disabled={isImporting}
                    className="ml-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                  >
                    {isImporting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Import...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4" />
                        Importer
                      </>
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </PageListLayout>
  );
}
